// Scenario loader (IMPLEMENTATION §3 M3 "Scenarios in shared/scenarios/", later `?scenario=` in the app, M5).
// A scenario is plain JSON: a start board (the cold-open layout or an empty board of an expansion mask), the
// unlock flags, cash, buildings and loose items placed in list order (ids ascend in that order, which is
// also the processing order), timed commands and a run length; `expect` holds the thresholds its test
// checks. Pure: callers pass the parsed JSON (tests read files, the app imports them).
//   unlocks: "all" (default) · "none" · an explicit list (M4a: start mid-progression; it must be
//   prefix-closed along each unlock's `after`). On a coldOpen board the listed unlocks are applied with
//   their effects (u01 empties the practice tin and raises the Lead Vein); on an empty board they are
//   recorded as flags only. The trigger engine carries on from there.
//   A command runs before tick t + 1 (like a log row); with `every` it repeats every `every` ticks while
//   t ≤ `until` (default: the whole run). Commands due in the same tick run in list order.
//   Command shorthands (resolved when the command is applied, from the live state):
//     "at": [x, y]                   → `bid` = the building on that cell
//     "from": {"k":"cell", x, y, "id": "@"} → `id` = the entity on the source cell
import type { CmdResult, Command } from './commands.ts';
import type { Int } from './core/types.ts';
import type { SimDefs } from './defs.ts';
import { findBuilding, findItem } from './entities.ts';
import {
  EXPANSION_IDS,
  type ExpansionId,
  type FamId,
  UNLOCK_IDS,
  type UnlockId,
} from './gen/ids.ts';
import { buildOcc, occAt } from './occupancy.ts';
import { Simulation } from './Simulation.ts';
import {
  cloneState,
  coldOpenState,
  emptyState,
  grantFlags,
  type PlaceSpec,
  placeBuilding,
  placeItem,
} from './setup.ts';
import type { SimState } from './state.ts';

export interface ScenarioItem {
  fam: FamId;
  tier: Int;
  x: Int;
  y: Int;
}

export interface ScenarioCommand {
  t: Int;
  every?: Int;
  until?: Int;
  cmd: Record<string, unknown>;
}

export interface ScenarioDef {
  name: string;
  description?: string;
  seed: Int;
  board: ExpansionId;
  /** `coldOpen`: the u00 layout (Gallery at its pre-placed cell); `empty` (default): nothing placed. */
  start?: 'coldOpen' | 'empty';
  /**
   * `all` (default): every version-active unlock as a flag (coldOpen: with its state effects); `none`; or
   * an explicit list of unlock ids (mid-progression start).
   */
  unlocks?: 'all' | 'none' | UnlockId[];
  cash?: Int;
  buildings?: PlaceSpec[];
  items?: ScenarioItem[];
  commands?: ScenarioCommand[];
  ticks: Int;
  /** Test thresholds (read by the scenario's test). */
  expect?: Record<string, unknown>;
}

function fail(name: string, msg: string): never {
  throw new Error(`scenario ${name}: ${msg}`);
}

/** Structural check of a parsed scenario (throws with the offending field). */
export function checkScenario(raw: unknown): ScenarioDef {
  if (typeof raw !== 'object' || raw === null) throw new Error('scenario: not an object');
  const s = raw as Partial<ScenarioDef>;
  const name = typeof s.name === 'string' && s.name.length > 0 ? s.name : fail('?', 'name missing');
  if (!Number.isSafeInteger(s.seed)) fail(name, 'seed must be an integer');
  if (!EXPANSION_IDS.includes(s.board as ExpansionId))
    fail(name, `unknown board ${String(s.board)}`);
  if (!Number.isSafeInteger(s.ticks) || (s.ticks as Int) < 0) fail(name, 'ticks must be ≥ 0');
  if (s.start !== undefined && s.start !== 'coldOpen' && s.start !== 'empty')
    fail(name, 'bad start');
  const u = s.unlocks;
  if (u !== undefined && u !== 'all' && u !== 'none') {
    if (!Array.isArray(u) || u.some((id) => typeof id !== 'string' || !UNLOCK_IDS.includes(id)))
      fail(name, 'unlocks must be "all", "none" or a list of unlock ids');
  }
  for (const c of s.commands ?? []) {
    if (!Number.isSafeInteger(c.t) || c.t < 0) fail(name, 'command t must be an integer ≥ 0');
    if (c.every !== undefined && (!Number.isSafeInteger(c.every) || c.every < 1)) {
      fail(name, `command at t=${c.t}: every must be ≥ 1`);
    }
    if (c.until !== undefined && !Number.isSafeInteger(c.until))
      fail(name, 'until must be an integer');
    if (typeof c.cmd !== 'object' || c.cmd === null)
      fail(name, `command at t=${c.t} is not an object`);
  }
  return s as ScenarioDef;
}

/** The start state of a scenario (before any command). */
export function scenarioState(d: SimDefs, def: ScenarioDef): SimState {
  const bounds = d.board.expansions.findIndex((e) => e.id === def.board);
  if (bounds < 0) fail(def.name, `board ${def.board} is not in the defs`);
  const unlocks = def.unlocks ?? 'all';
  const list = Array.isArray(unlocks) ? unlocks : undefined;
  let s: SimState;
  try {
    if (def.start === 'coldOpen') {
      s = coldOpenState(d, def.seed, {
        bounds,
        unlockAll: unlocks === 'all',
        ...(list !== undefined ? { unlocked: list } : {}),
      });
    } else {
      s = emptyState(d, def.seed, bounds);
      if (unlocks === 'all') grantFlags(s, d);
      else if (list !== undefined) grantFlags(s, d, list);
    }
  } catch (err) {
    fail(def.name, `unlocks: ${(err as Error).message}`);
  }
  for (const b of def.buildings ?? []) {
    try {
      placeBuilding(s, d, b);
    } catch (err) {
      fail(def.name, `building ${b.type} at (${b.x},${b.y}): ${(err as Error).message}`);
    }
  }
  for (const it of def.items ?? []) placeItem(s, d, it.fam, it.tier, it.x, it.y);
  if (def.cash !== undefined) s.wallet.cash = def.cash;
  return s;
}

/** Resolves the scenario command shorthands against the live state. */
export function resolveCommand(s: SimState, d: SimDefs, raw: Record<string, unknown>): Command {
  const cmd = JSON.parse(JSON.stringify(raw)) as Record<string, unknown>;
  const occ = buildOcc(s, d);
  const at = cmd.at;
  if (Array.isArray(at)) {
    const id = occAt(occ, at[0] as Int, at[1] as Int);
    cmd.bid = id > 0 && findBuilding(s, id) !== null ? id : 0;
    delete cmd.at;
  }
  const from = cmd.from as Record<string, unknown> | undefined;
  if (from && from.k === 'cell' && from.id === '@') {
    const id = occAt(occ, from.x as Int, from.y as Int);
    from.id = id > 0 && (findItem(s, id) !== null || findBuilding(s, id) !== null) ? id : 0;
  }
  return cmd as unknown as Command;
}

/** True when scenario command `c` runs before tick t + 1. */
export function isDue(c: ScenarioCommand, t: Int): boolean {
  if (c.every === undefined) return c.t === t;
  return t >= c.t && (c.until === undefined || t <= c.until) && (t - c.t) % c.every === 0;
}

export interface ScenarioRun {
  sim: Simulation;
  initial: SimState;
}

export interface ScenarioHooks {
  /** After every tick. */
  onTick?: (sim: Simulation) => void;
  /** After every command, with its result. */
  onCommand?: (sim: Simulation, cmd: Command, r: CmdResult) => void;
  /** Run length (default `def.ticks`). */
  ticks?: Int;
}

/**
 * Runs a scenario: its commands are applied before tick t + 1 (like a log). Returns the simulation after
 * the run and the start state.
 */
export function runScenario(d: SimDefs, def: ScenarioDef, hooks: ScenarioHooks = {}): ScenarioRun {
  const initial = scenarioState(d, def);
  const sim = new Simulation(d, cloneState(initial));
  const cmds = def.commands ?? [];
  const ticks = hooks.ticks ?? def.ticks;
  for (let t = 0; t < ticks; t++) {
    for (const c of cmds) {
      if (!isDue(c, t)) continue;
      const cmd = resolveCommand(sim.state, d, c.cmd);
      const r = sim.apply(cmd);
      hooks.onCommand?.(sim, cmd, r);
    }
    sim.tick();
    hooks.onTick?.(sim);
  }
  return { sim, initial };
}
